<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Lock } from 'lucide-vue-next'

interface AppLogoProps {
  // 配置选项
  logoUrl?: string
  logoText?: string
  title?: string

  // 显示模式
  mode?: 'default' | 'icon'

  // 尺寸
  size?: number
}

const props = withDefaults(defineProps<AppLogoProps>(), {
  mode: 'default',
  size: 134.4,
})

const logoError = ref(false)
const logoImgRef = ref<HTMLImageElement | null>(null)

const onLogoError = (e: Event) => {
  console.error('Logo加载失败:', props.logoUrl)
  logoError.value = true
}

const onLogoLoad = () => {
  console.log('Logo图片加载成功!')
}

// 显示图标模式
const showIconMode = props.mode === 'icon'

// 显示文字模式（没有图片或图片加载失败）
const showTextMode = !showIconMode && (!props.logoUrl || logoError.value)

// Logo 容器大小
const containerSize = `${props.size}px`
const innerSize = `${props.size * 0.952}px` // 128/134.4 ≈ 0.952

onMounted(() => {
  console.log('=== AppLogo Component Debug ===')
  console.log('Mode:', props.mode)
  console.log('Logo URL:', props.logoUrl)
  console.log('Logo Text:', props.logoText)
  console.log('Title:', props.title)
  console.log('Show Icon Mode:', showIconMode)
  console.log('Show Text Mode:', showTextMode)
})
</script>

<template>
  <div
    class="relative flex items-center justify-center"
    :style="{ width: containerSize, height: containerSize }"
  >
    <!-- 渐变边框 -->
    <div class="absolute inset-0 p-[3px] rounded-full gradient-border-mask opacity-50">
      <!-- 图标模式：整个渐变背景作为容器 -->
      <div
        v-if="showIconMode"
        class="w-full h-full bg-gradient-to-br from-[#4A7DFF] via-[#8E6DFF] to-[#E55DFF] rounded-full shadow-[0_14.4px_42px_rgba(142,109,255,0.3),_inset_0_-9.6px_18px_rgba(142,109,255,0.4),_inset_0_9.6px_18px_rgba(255,255,255,0.2)] flex flex-col items-center justify-center text-white relative"
      >
        <Lock :size="50" stroke-width="1.5" class="relative z-10" />
      </div>

      <!-- 默认模式：只有渐变边框，内容在中间 -->
      <div
        v-if="!showIconMode"
        class="w-full h-full bg-gradient-to-br from-[#4A7DFF] via-[#8E6DFF] to-[#E55DFF] rounded-full shadow-[0_14.4px_42px_rgba(142,109,255,0.3),_inset_0_-9.6px_18px_rgba(142,109,255,0.4),_inset_0_9.6px_18px_rgba(255,255,255,0.2)]"
      ></div>
    </div>

    <!-- 内容区域（仅用于默认模式） -->
    <div
      v-if="!showIconMode"
      class="relative z-10 flex items-center justify-center"
      :style="{ width: innerSize, height: innerSize }"
    >
      <!-- 图片模式：显示配置的 logoUrl -->
      <div
        v-if="!showTextMode"
        class="w-full h-full bg-white rounded-full flex items-center justify-center shadow-lg overflow-hidden"
      >
        <img
          ref="logoImgRef"
          :src="logoUrl"
          :alt="logoText || title"
          class="w-full h-full object-cover"
          @error="onLogoError"
          @load="onLogoLoad"
        />
      </div>

      <!-- 文字模式：显示 logoText 或默认 LOGO -->
      <div v-else class="flex flex-col items-center text-white">
        <span class="text-[21.6px] font-black tracking-tighter mb-0.5">
          {{ logoText || 'LOGO' }}
        </span>
        <svg
          width="21.6"
          height="21.6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gradient-border-mask {
  background: linear-gradient(135deg, #1e4fd9 0%, #8e6dff 50%, #e65f00 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}
</style>
